// 📄 pages/Perfil/MiPerfil.tsx
import { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { User as UserIcon, Mail, Globe2, Save, KeyRound, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { updateOwnProfile } from '../../services/userService.ts';
import { getDisplayName, getInitials } from '../../utils/displayName.ts';
import { PAISES } from '../../data/paises.ts';
import MisAusenciasSection from './MisAusenciasSection.tsx';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

export default function MiPerfil() {
  const { user, profile } = useAuth();

  const [nombre, setNombre] = useState(profile?.name || user?.displayName || '');
  const [pais, setPais] = useState(profile?.pais || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [passwordActual, setPasswordActual] = useState('');
  const [passwordNueva, setPasswordNueva] = useState('');
  const [passwordConfirmar, setPasswordConfirmar] = useState('');
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  const [errorPassword, setErrorPassword] = useState<string | null>(null);
  const [successPassword, setSuccessPassword] = useState<string | null>(null);

  if (!user) return null;

  const displayName = getDisplayName(user, { name: nombre });
  const initials = getInitials(displayName);

  const handleGuardarPerfil = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!nombre.trim()) { setError('El nombre no puede quedar vacío.'); return; }

    setGuardando(true);
    try {
      await updateOwnProfile(user.uid, { name: nombre, pais });
      setSuccess('Perfil actualizado correctamente.');
    } catch (err: any) {
      setError(err.message || 'Error al guardar el perfil.');
    } finally {
      setGuardando(false);
    }
  };

  const handleCambiarPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorPassword(null);
    setSuccessPassword(null);

    if (passwordNueva.length < 6) { setErrorPassword('La nueva contraseña debe tener al menos 6 caracteres.'); return; }
    if (passwordNueva !== passwordConfirmar) { setErrorPassword('Las contraseñas nuevas no coinciden.'); return; }
    if (!user.email) { setErrorPassword('Esta cuenta no tiene email asociado.'); return; }

    setCambiandoPassword(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, passwordActual);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwordNueva);
      setPasswordActual('');
      setPasswordNueva('');
      setPasswordConfirmar('');
      setSuccessPassword('Contraseña actualizada correctamente.');
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setErrorPassword('La contraseña actual no es correcta.');
      } else {
        setErrorPassword(err.message || 'Error al cambiar la contraseña.');
      }
    } finally {
      setCambiandoPassword(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <span className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-xl font-bold text-white flex-shrink-0">
          {initials}
        </span>
        <div>
          <h1 className="text-xl font-semibold text-white">Mi perfil</h1>
          <p className="text-sm text-gray-400">Gestiona tu información personal y tu contraseña.</p>
        </div>
      </div>

      {/* Datos personales */}
      <form onSubmit={handleGuardarPerfil} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <UserIcon className="w-4 h-4 text-blue-400" /> Datos personales
        </h2>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        )}
        {success && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{success}</div>
        )}

        <div>
          <label className={labelClass}>Nombre</label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Tu nombre completo"
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Correo</span>
          </label>
          <input type="email" value={user.email || ''} disabled className={`${inputClass} opacity-60 cursor-not-allowed`} />
        </div>

        <div>
          <label className={labelClass}>
            <span className="inline-flex items-center gap-1"><Globe2 className="w-3 h-3" /> País de residencia</span>
          </label>
          <select value={pais} onChange={(e) => setPais(e.target.value)} className={inputClass}>
            <option value="">Selecciona un país</option>
            {PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <p className="text-[11px] text-gray-500 mt-1">Se usa para coordinar horarios y reuniones de equipo.</p>
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={guardando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-[#fff] transition-all"
          >
            <Save className="w-4 h-4" />
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      {/* Cambiar contraseña */}
      <form onSubmit={handleCambiarPassword} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-blue-400" /> Cambiar contraseña
        </h2>

        {errorPassword && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorPassword}</div>
        )}
        {successPassword && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{successPassword}</div>
        )}

        <div>
          <label className={labelClass}>Contraseña actual</label>
          <input
            type="password"
            value={passwordActual}
            onChange={(e) => setPasswordActual(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Nueva contraseña</label>
            <input
              type="password"
              value={passwordNueva}
              onChange={(e) => setPasswordNueva(e.target.value)}
              className={inputClass}
              minLength={6}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Confirmar nueva contraseña</label>
            <input
              type="password"
              value={passwordConfirmar}
              onChange={(e) => setPasswordConfirmar(e.target.value)}
              className={inputClass}
              minLength={6}
              required
            />
          </div>
        </div>

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={cambiandoPassword}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-[#fff] transition-all"
          >
            {cambiandoPassword ? <RefreshCw className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {cambiandoPassword ? 'Cambiando...' : 'Cambiar contraseña'}
          </button>
        </div>
      </form>

      <MisAusenciasSection uid={user.uid} />
    </div>
  );
}
